// Time Zone Intelligence — read a peer's IANA timezone + working hours off their
// PublicUser and render "their local time" + whether they're inside working hours.
// Browser-side only (Intl); everything degrades to null when tz isn't set.

export type TzUser = {
  timezone?: string | null;
  work_start_min?: number | null;
  work_end_min?: number | null;
  work_days?: string | null; // csv, 0=Sun..6=Sat
  name?: string | null;
};

/** "3:14 PM" in the peer's zone, or null if no/invalid tz. */
export function peerLocalTime(tz?: string | null, now: Date = new Date()): string | null {
  if (!tz) return null;
  try {
    return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit', timeZone: tz }).format(now);
  } catch {
    return null;
  }
}

const WD: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

/** Local minutes-past-midnight + weekday (0=Sun) in a given tz, or null. */
function localMinuteAndDay(tz: string, now: Date): { min: number; day: number } | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      weekday: 'short',
    }).formatToParts(now);
    const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? '0') % 24;
    const minute = Number(parts.find((p) => p.type === 'minute')?.value ?? '0');
    const day = WD[parts.find((p) => p.type === 'weekday')?.value ?? ''] ?? now.getDay();
    return { min: hour * 60 + minute, day };
  } catch {
    return null;
  }
}

/** True if the peer is currently inside their configured working hours + days.
 *  null when we don't have enough of their schedule to say. */
export function withinWorkHours(u: TzUser, now: Date = new Date()): boolean | null {
  if (!u.timezone || u.work_start_min == null || u.work_end_min == null) return null;
  const info = localMinuteAndDay(u.timezone, now);
  if (!info) return null;
  const days = (u.work_days || '1,2,3,4,5')
    .split(',')
    .map((s) => Number(s.trim()))
    .filter((n) => !Number.isNaN(n));
  if (days.length && !days.includes(info.day)) return false;
  const s = u.work_start_min;
  const e = u.work_end_min;
  // normal window, or one that wraps past midnight (e.g. a night shift)
  return s <= e ? info.min >= s && info.min < e : info.min >= s || info.min < e;
}

/** A short header hint like "· 3:14 PM local" or "· 11:40 PM · off hours". */
export function localTimeHint(u: TzUser, now: Date = new Date()): string | null {
  const t = peerLocalTime(u.timezone, now);
  if (!t) return null;
  const ok = withinWorkHours(u, now);
  return ok === false ? `${t} · off hours` : `${t} local`;
}
